import { StoreProduct } from '../services/store-billing.service';

/**
 * Plan price presentation shared by the payment page and the results paywall.
 * Every amount comes from the admin payment link (web) or the store (native);
 * nothing here holds a price. The savings figure is only produced when both
 * plans are priced on the same platform in the same currency, and it is rounded
 * DOWN so the claim is never larger than the real saving.
 */

export const round2 = (n: number): number => Math.round(n * 100) / 100;

/** A positive finite number, else null. */
export function priceNum(v: unknown): number | null {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Whole-percent saving of a yearly charge vs twelve monthly charges, or null. */
export function savePct(yearly: number | null, monthly: number | null): number | null {
  if (!yearly || !monthly) return null;
  const pct = Math.floor((1 - yearly / (monthly * 12)) * 100);
  return pct >= 1 ? pct : null;
}

/** Web annual plan: the admin stores the monthly equivalent; PayPal charges it
 *  x12 rounded to cents (PaypalService.ensurePlan), so the total uses the same math. */
export function webAnnualTotal(annualMonthly: number | null): number | null {
  return annualMonthly ? round2(annualMonthly * 12) : null;
}

/** Locale-formatted store amount, e.g. 12.91 ILS -> "₪12.91" / "‏12.91 ₪". */
export function fmtStoreAmount(amount: number, currency: string, rtl: boolean): string {
  try {
    return new Intl.NumberFormat(rtl ? 'he-IL' : 'en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

/** Store annual/monthly pair priced in the same currency, else null (no claim). */
export function storePair(
  annual?: StoreProduct,
  monthly?: StoreProduct,
): { yearly: number; monthly: number; currency: string } | null {
  if (!annual || !monthly || !annual.currency || annual.currency !== monthly.currency) return null;
  const y = priceNum(annual.priceAmount);
  const m = priceNum(monthly.priceAmount);
  return y && m ? { yearly: y, monthly: m, currency: annual.currency } : null;
}
